/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import keyBy from 'lodash/keyBy';
import { CARD_TYPES, CARD_ACTIONS } from 'carbon-addons-iot-react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import PubSub from 'pubsub-js';

import { PickerKinds, TimeRanges } from '../../types/DateTypes';
import {
  determineDashboardStartDate,
  determineDefaultPresets,
} from '../../../Dashboard/utils/dashboardRendererUtils';
import {
  determineCardDataGrain,
  determineStartDate,
  shouldCardIgnoreDashboard,
  determineStartDateFromPresetRangeChange,
  determineCardDataStartDate,
  generateEndDate,
} from '../../utils/cardUtilityFunctions';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { CUSTOM_CARD_TYPES, DataItemsPropTypes } from '../../constants/constants';

import CardMapper from './CardMapper';
import CardFetcher from './CardFetcher';

import { formatDefaultCardRange } from 'monitoringUtils';
import { getIconByName } from 'iconUtils';

//  CardRenderer -> CardFetcher -> CardMapper -> Card
/**
 * This component decides which card component to render when passed a certain card type.
 * It keeps the these local states
 * range - the currently selected range string from the card range picker
 * isExpanded - whether the card is fullscreen or not
 *
 * It also caches some properties between renders to speed performance
 *
 */
const CardRenderer = React.memo(
  ({
    i18n,
    isEditable,
    breakpoint,
    onFetchData,
    onDataFetched,
    onCardRender,
    dashboardTimeGrain,
    hasInstanceDashboard,
    deviceId,
    dataItems,
    entityType,
    filter,
    ...cardProp
  }) => {
    // pass through the card props
    const { dataSource, availableActions, type } = cardProp;
    const { formatMessage } = useIntl();

    /** Callback called when a card determines what icon render based on a named string in card config
     *    example usage: renderIconByName(name = 'my--checkmark--icon', props = { title: 'A checkmark', etc. }) */
    const cachedRenderIconByName = useCallback(
      (name, props = {}) => React.createElement(getIconByName(name), props),
      []
    );

    // if there's not a range or a timeRange we should hide the date
    const [showIconOnly, setShowIconOnly] = useState(
      () => !(dataSource?.range || cardProp?.timeRange),
      [cardProp?.timeRange, dataSource?.range]
    );

    /**
     * Local state for the card, keeps track of whether it is loading or not, and the current state of the Range Selector
     * And which data range is being requested.
     */
    // Can't get rid of this full card, setCard state until we rewrite the Image Card setupCard
    const [card, setCard] = useState(cardProp);
    // keep track of the expanded card id
    const [isExpanded, setIsExpanded] = useState(false);
    // the card expects the default presets to be keyed by the id
    const timeRangeOptions = useMemo(
      () => keyBy(determineDefaultPresets(formatMessage, card?.dataSource?.range), 'id'),
      [formatMessage, card?.dataSource?.range]
    );
    // selectedTimeRange is the selected time range for a given card
    const [selectedTimeRange, setSelectedTimeRange] = useState(
      timeRangeOptions[card.timeRange] // if the card string time range matches a preset use it
        ? {
            timeRangeKind: PickerKinds.PRESET,
            timeRangeValue: timeRangeOptions[card.timeRange],
          }
        : card?.dataSource?.range // if a range is set then set to default
        ? {
            timeRangeKind: PickerKinds.PRESET,
            timeRangeValue: timeRangeOptions[TimeRanges.DEFAULT],
          }
        : {
            timeRangeKind: PickerKinds.PRESET,
            timeRangeValue: timeRangeOptions[TimeRanges.LAST_24_HOURS],
          }
    );

    // Keeps track of the start, end and grain that the data fetcher is going to use
    // has to be in stored in one state to stop triggering fetching useEffect twice!
    // keep local state because it could differ from the dashboard if the user uses the DashboardDateSelector in the card
    const [dataFetchParams, setDataFetchParams] = useState(() => {
      // if we have endDate in query parameters, then we are going to receive an event to fetch with those params
      // in which case we should skip the default (7 days) request
      const { searchParams } = new URL(document.location);

      // dashboard will always clear out the seconds/milliseconds so we need to start from that as well to make sure we don't trigger an extra fetch
      const now = generateEndDate();
      return searchParams.get('endDate')
        ? {
            endDate: null,
            startDate: null,
            grain: null,
          }
        : {
            endDate: now,
            // is there a selected timeRange?
            startDate: card?.timeRange
              ? determineStartDateFromPresetRangeChange(now, card?.timeRange)
              : // if there a defined JSON
              card?.dataSource?.range
              ? determineStartDate(card?.dataSource?.range, now)
              : // else inherit from the dashboard
                determineDashboardStartDate(now, dashboardTimeGrain),

            // If the card has a grain in it's JSON, then use it as the default, we will dynamically change if the start and end date changes
            grain: card?.dataSource?.timeGrain,
          };
    });

    // Stores the listener token for the pub sub
    const dashboardListener = useRef();

    const unsubscribeFromAllDashboardEvents = useCallback(() => {
      if (dashboardListener.current) {
        PubSub.unsubscribe(dashboardListener.current);
      }
    }, [dashboardListener]);

    // subscribe to dashboard events and keep track of the event tokens given by PubSub so that we can unsubscribe to the event listeners when needed
    // we need to always subscribe to endDate no matter the card details
    useEffect(() => {
      if (dashboardListener) {
        dashboardListener.current = PubSub.subscribe('fetchParamsChanged', (event, payload) => {
          const {
            startDate: startDateFromDashboard,
            endDate: endDateFromDashboard,
            timeGrain: timeGrainFromDashboard,
            timeRange: timeRangeFromDashboard,
            shouldCardsListenToDashboardDateChanges,
          } = payload;
          const isCardListening =
            shouldCardsListenToDashboardDateChanges ||
            !shouldCardIgnoreDashboard(card?.dataSource?.range, card?.timeRange, selectedTimeRange);
          // should the card listen to the dashboard or not?
          if (
            !isCardListening &&
            (selectedTimeRange.timeRangeKind !== PickerKinds.PRESET || // only the presets are relative
              // only PRESET should verify the end date before ignoring
              !endDateFromDashboard ||
              endDateFromDashboard?.isSame(dataFetchParams.endDate))
          ) {
            onDataFetched(card.id);
          } else {
            // If we're listening to the dashboard update our visible timeRange
            if (isCardListening) {
              // we need to update the selected time range so it shows if we pop out the date selector (even though we're not showing it in the top tooltip)
              setSelectedTimeRange(timeRangeFromDashboard);
              // If we're overridden again by the dashboard we need to hide the icon again
              setShowIconOnly(true);
            }
            // updating these parameters will force a data refresh in the lower component
            setDataFetchParams(currentDataFetchParams => ({
              startDate: isCardListening // if the card is listening to the dashboard then follow it's start date
                ? startDateFromDashboard
                : determineCardDataStartDate(
                    // otherwise, figure out from the card information
                    startDateFromDashboard,
                    endDateFromDashboard || currentDataFetchParams.endDate,
                    selectedTimeRange,
                    card?.dataSource?.range,
                    card?.timeRange
                  ),
              endDate: endDateFromDashboard || currentDataFetchParams.endDate,
              // If the user has interacted with the card range manually, use the old grain
              grain:
                isCardListening && timeGrainFromDashboard
                  ? determineCardDataGrain(card?.dataSource?.timeGrain, timeGrainFromDashboard)
                  : currentDataFetchParams.grain,
            }));
          }
        });
      }

      // Unsubscribe to all listeners on unmount
      return () => unsubscribeFromAllDashboardEvents();
    }, [
      card.id,
      card?.dataSource?.range,
      card?.dataSource?.timeGrain,
      dataFetchParams.endDate,
      onDataFetched,
      selectedTimeRange,
      unsubscribeFromAllDashboardEvents,
      card?.timeRange,
    ]);

    // Allow the i18n to change between renders to show the default card range
    const cachedI18N = useMemo(
      () => ({ ...i18n, defaultLabel: formatDefaultCardRange(dataSource?.range) }),
      [i18n, dataSource?.range]
    );

    // all cards with datasource should have the range picker
    const hasRange = !isEmpty(card.dataSource) || type === CUSTOM_CARD_TYPES.THIRD_PARTY;

    // Speed up performance by caching the available actions
    const cachedActions = useMemo(
      () =>
        merge(availableActions, {
          range: hasRange ? (showIconOnly ? 'iconOnly' : 'full') : false,
          expand:
            type === CARD_TYPES.IMAGE ||
            type === CARD_TYPES.TIMESERIES ||
            type === CARD_TYPES.TABLE ||
            type === CARD_TYPES.BAR ||
            type === CUSTOM_CARD_TYPES.ALERT ||
            type === CUSTOM_CARD_TYPES.THIRD_PARTY ||
            availableActions?.expand, // image and line chart cards should have expand
          extra: type === CUSTOM_CARD_TYPES.THIRD_PARTY,
        }),
      [availableActions, type, showIconOnly, hasRange]
    );

    /**
     * Listen to the card's range action to decide whether to trigger a data refetch
     */
    const cachedOnCardAction = useCallback((id, actionType, payload) => {
      switch (actionType) {
        case CARD_ACTIONS.CHANGE_TIME_RANGE: {
          const newEndDate = generateEndDate();
          // Once the user changes the time range we need to show the icon
          setShowIconOnly(false);
          // the payload comes back with extra start/end params
          setSelectedTimeRange({
            ...payload,
            timeRangeValue: omit(payload.timeRangeValue, 'start', 'end'),
          });
          // For each type of range selection we need to correctly set the start and end date
          // ABSOLUTE and RELATIVE pass the dates as end/start, PRESETS give us the offset
          setDataFetchParams(currentDataFetchParams => ({
            ...currentDataFetchParams,
            endDate: payload?.timeRangeValue?.end
              ? moment(payload?.timeRangeValue?.end).set({ seconds: 0, milliseconds: 0 }) // workaround for relative passing seconds and milliseconds
              : newEndDate,
            startDate: payload?.timeRangeValue?.start
              ? moment(payload?.timeRangeValue?.start).set({ seconds: 0, milliseconds: 0 }) // workaround for relative passing seconds and milliseconds
              : newEndDate.clone().subtract(payload?.timeRangeValue?.offset, 'minutes'),
          }));
          break;
        }

        case CARD_ACTIONS.OPEN_EXPANDED_CARD: {
          setIsExpanded(true);
          // If this is a time-based chart card with raw metric values, re-load the card data because the time may need to change
          break;
        }

        case CARD_ACTIONS.CLOSE_EXPANDED_CARD: {
          setIsExpanded(false);
          // If this is a time-based chart card, enable the zoombar
          break;
        }

        // use this to reload custom cards with data TODO: We cannot remove the full setCard state here unless we move this down somewhere and rewrite
        case CARD_ACTIONS.UPDATE_DATA: {
          // New action type where we pass the updated values data
          setCard(currentCardState => ({
            ...currentCardState,
            // support setting any field of the card
            ...payload,
          }));
          break;
        }

        default: {
          console.error('actionType not found ', actionType);
        }
      }
    }, []);

    const cardWithAddlData = useMemo(
      () => (onCardRender ? onCardRender(card) : card),
      [card, onCardRender]
    );

    const commonCardProps = useMemo(
      () => ({
        ...cardWithAddlData, // pass all the card props, including the card data to the card
        // if card is editable, meaning its in a dashboard editor, listen to card prop
        // changes and reset the local card state
        ...(isEditable ? { ...cardProp } : {}),
        ...((cardProp.type === CARD_TYPES.CUSTOM ||
          cardProp.type === CUSTOM_CARD_TYPES.ALERT ||
          cardProp.type === CUSTOM_CARD_TYPES.THIRD_PARTY) &&
        cardProp.content
          ? { content: cardProp.content }
          : {}), // the card prop needs to be used instead of the cached state property for custom cards
        dataSource,
        timeRange: selectedTimeRange,
        interval: dataSource?.timeGrain, // the x-axis tick format for line series cards should match the dataSource grain
        style: cardProp.style, // these come from grid layout and not state
        className: cardProp.className, // these come from grid layout and not state
        availableActions: cachedActions,
        isExpanded,
        i18n: cachedI18N,
        isEditable,
        // if editable, allow the passed onCardAction as we only care about the edit and delete actions
        onCardAction: isEditable ? cardProp.onCardAction : cachedOnCardAction,
        renderIconByName: cachedRenderIconByName,
        breakpoint,
        timeRangeOptions,
        // re-map the card description to the card tooltip
        tooltip: cardProp.description,
      }),
      [
        cardWithAddlData,
        isEditable,
        cardProp,
        dataSource,
        selectedTimeRange,
        cachedActions,
        isExpanded,
        cachedI18N,
        cachedOnCardAction,
        cachedRenderIconByName,
        breakpoint,
        timeRangeOptions,
      ]
    );

    return (
      <ErrorBoundary>
        <CardFetcher
          card={commonCardProps}
          setCard={setCard}
          startDate={dataFetchParams.startDate}
          endDate={dataFetchParams.endDate}
          onFetchData={onFetchData}
          onDataFetched={onDataFetched}
          grain={dataFetchParams.grain}
          dataItems={dataItems}
          filter={filter}>
          <CardMapper
            entityType={entityType}
            cardProps={commonCardProps}
            hasInstanceDashboard={hasInstanceDashboard}
            deviceId={deviceId}
          />
        </CardFetcher>
      </ErrorBoundary>
    );
  }
);

CardRenderer.propTypes = {
  isEditable: PropTypes.bool,
  breakpoint: PropTypes.oneOf(['max', 'xl', 'lg', 'md', 'sm', 'xs']),
  /**
   * Allows the card to change it's cardConfig from the initial dashboard template
   * to add necessary information for the DashboardGrid to render
   */
  onCardRender: PropTypes.func,
  /** The dashboard has determined that the card needs to refresh it's data */
  needsDataRefresh: PropTypes.bool, // eslint-disable-line react/boolean-prop-naming
  /** callback to notify the dashboard that the data fetch is finished */
  onDataFetched: PropTypes.func.isRequired,
  /** the card mapper needs these dataItems to figure out how to format the values */
  dataItems: DataItemsPropTypes,
  onFetchData: PropTypes.func,
  onSetupCard: PropTypes.func,
  dashboardTimeGrain: PropTypes.string,
  hasDashboardTimeGrainChanged: PropTypes.bool,
  i18n: PropTypes.shape({
    last7DaysLabel: PropTypes.string,
    last24HoursLabel: PropTypes.string,
    last8HoursLabel: PropTypes.string,
    last4HoursLabel: PropTypes.string,
    last2HoursLabel: PropTypes.string,
    lastHourLabel: PropTypes.string,
  }).isRequired,
  hasInstanceDashboard: PropTypes.bool,
  /** need to know the entity Type for the instance dashboard links */
  entityType: PropTypes.string,
  deviceId: PropTypes.string,
  filter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};
CardRenderer.defaultProps = {
  dataItems: [],
  hasDashboardTimeGrainChanged: false,
  onCardRender: null,
  needsDataRefresh: false,
  isEditable: false,
  breakpoint: 'max',
  onFetchData: null,
  onSetupCard: null,
  dashboardTimeGrain: null,
  hasInstanceDashboard: false,
  entityType: null,
  deviceId: null,
  filter: null,
};
CardRenderer.displayName = 'CardRenderer';

export default CardRenderer;
